import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Divider,
  Input,
  Select,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";

import { ClearOutlined } from "@ant-design/icons";

import { ApiError, type UserRow, api } from "../api/client";
import { AddUserModal } from "../components/AddUserModal";
import { QuickFilterDropdown } from "../components/QuickFilterDropdown";
import { formatCents, formatCompactDateTime, formatFullIso } from "../utils/dateFormat";

const { Title, Text } = Typography;
// Operator cap is ~200 users. Backend caps page_size at 500, which we
// pin to so the list is fetched in a single round-trip and the AntD
// table renders without a pager.
const PAGE_SIZE = 500;

type Filter = "all" | "regular" | "guest";

type SortField =
  | "name"
  | "balance"
  | "created"
  | "updated"
  | "email"
  | "real_name"
  | "last_activity"
  | "cost";
type SortDir = "asc" | "desc";

/**
 * UsersPage — master/detail finder per the user-management-flow spec.
 *
 * Conventions baked in (`feedback-admin-table-baseline`):
 *   - every column sortable + per-column filter
 *   - sticky header
 *   - compact `DD.MM HH:MM` timestamps with full ISO on hover
 *   - filter + sort state mirrored into the URL query string
 *   - row click navigates to `/users/{strichliste_user_id}`
 */
export const UsersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = (searchParams.get("type") as Filter) || "all";
  const q = searchParams.get("q") ?? "";
  const sort = (searchParams.get("sort") as SortField | null) ?? undefined;
  const sortDir = (searchParams.get("sort_dir") as SortDir | null) ?? "asc";
  const statusFilter = searchParams.get("status") as
    | "active"
    | "inactive"
    | "disabled"
    | null;
  // Voucher users are hidden by default (they're a kiosk-only construct); the
  // toggle reveals them via the backend's show_vouchers flag.
  const showVouchers = searchParams.get("vouchers") === "1";

  const [rows, setRows] = useState<UserRow[]>([]);
  const [owedMap, setOwedMap] = useState<Map<number, number>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // Stage q in a separate input so we can debounce.
  const [qDraft, setQDraft] = useState(q);

  // Any of: free-text search, type tab off-default, status filter,
  // explicit sort. We use the URL string (single source of truth) so
  // the Reset button mirrors what the user actually sees in the bar.
  const hasActiveFilters =
    !!q || filter !== "all" || !!sort || !!statusFilter || showVouchers;

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listUsers({
        user_type: filter === "all" ? undefined : filter,
        q: q || undefined,
        sort,
        sort_dir: sortDir,
        status: statusFilter ?? undefined,
        show_vouchers: showVouchers || undefined,
        include_cogs: true,
        page_size: PAGE_SIZE,
      });
      setRows(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, q, sort, sortDir, statusFilter, showVouchers]);

  // Volunteer owed-day counts for the column — one aggregate fetch (not per-row),
  // re-synced when the list reloads. (add-volunteer-compensation)
  useEffect(() => {
    let alive = true;
    api
      .getVolunteerOwedCounts()
      .then((r) => {
        if (!alive) return;
        setOwedMap(
          new Map(r.counts.map((c) => [c.strichliste_user_id, c.owed_count])),
        );
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [rows]);

  // Debounced sync: typing in the search box settles into the URL after a
  // brief pause so each keystroke doesn't re-request the list.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (qDraft) next.set("q", qDraft);
      else next.delete("q");
      next.delete("page");
      if (next.toString() !== searchParams.toString()) setSearchParams(next);
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === "") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const onTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserRow> | SorterResult<UserRow>[],
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const next = new URLSearchParams(searchParams);
    if (s && s.order) {
      // Use columnKey (column.key) rather than s.field — `field` defaults
      // to dataIndex, which for the Balance column is `balance_cents`,
      // not the backend's enum value `balance`. columnKey is the value
      // we set on the column object and matches the backend's SortField.
      next.set("sort", String(s.columnKey ?? s.field));
      next.set("sort_dir", s.order === "ascend" ? "asc" : "desc");
    } else {
      next.delete("sort");
      next.delete("sort_dir");
    }
    const typeFilter = filters.user_type as string[] | null;
    if (typeFilter && typeFilter.length === 1) {
      next.set("type", typeFilter[0]);
    } else {
      next.delete("type");
    }
    const statusRaw = filters.status as string[] | null;
    if (statusRaw && statusRaw.length === 1) {
      next.set("status", statusRaw[0]);
    } else {
      next.delete("status");
    }
    // Clean up any legacy URL with the old `?disabled=` param.
    next.delete("disabled");
    setSearchParams(next);
  };

  const onInlineEdit = async (
    row: UserRow,
    patch: { real_name?: string; user_type?: "regular" | "guest" },
  ) => {
    try {
      await api.updateUser(row.strichliste_user_id, patch);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const columns: ColumnsType<UserRow> = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        sorter: true,
        sortOrder:
          sort === "name"
            ? sortDir === "asc"
              ? "ascend"
              : "descend"
            : null,
        render: (v: string, row) => (
          // flex container with `minWidth: 0` on the name lets it
          // truncate via ellipsis instead of pushing the note Tag into
          // the neighboring column on narrow viewports. The Tag itself
          // keeps `flexShrink: 0` so it never collapses.
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            <strong
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
              title={v}
            >
              {v}
            </strong>
            {(row.public_note || row.internal_note) && (
              <Tooltip
                title={
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {row.public_note && (
                      <div>
                        <strong>Public:</strong> {row.public_note}
                      </div>
                    )}
                    {row.public_note && row.internal_note && (
                      <Divider
                        style={{
                          margin: "6px 0",
                          borderColor: "rgba(255,255,255,0.25)",
                        }}
                      />
                    )}
                    {row.internal_note && (
                      <div>
                        <strong>Internal:</strong> {row.internal_note}
                      </div>
                    )}
                  </div>
                }
              >
                <Tag color="geekblue" style={{ flexShrink: 0, margin: 0 }}>
                  note
                </Tag>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Real name",
        dataIndex: "real_name",
        key: "real_name",
        responsive: ["md"],
        sorter: true,
        sortOrder:
          sort === "real_name"
            ? sortDir === "asc"
              ? "ascend"
              : "descend"
            : null,
        ellipsis: { showTitle: false },
        render: (v: string | null, row) => (
          <InlineText
            value={v ?? ""}
            placeholder="—"
            onSave={(next) => onInlineEdit(row, { real_name: next })}
          />
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        // Long-form text + secondary identifier — keep off until xl
        // where a full-desktop monitor has room.
        responsive: ["xl"],
        sorter: true,
        sortOrder:
          sort === "email"
            ? sortDir === "asc"
              ? "ascend"
              : "descend"
            : null,
        ellipsis: { showTitle: false },
        render: (v: string | null) => (
          <span
            title={v ?? undefined}
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {v ?? "—"}
          </span>
        ),
      },
      {
        title: "Type",
        dataIndex: "user_type",
        key: "user_type",
        width: 120,
        responsive: ["xl"],
        filterDropdown: (p) => (
          <QuickFilterDropdown
            props={p}
            options={[
              { value: "regular", label: "Regular" },
              { value: "guest", label: "Guest" },
            ]}
          />
        ),
        filteredValue: filter === "all" ? null : [filter],
        // Backend has no `user_type` sort key — the default sort
        // (guests-first then name-asc) is what the column already
        // expresses. Filter only.
        render: (v: string, row) =>
          row.user_type === "bank" ? (
            <Tag color="purple">bank</Tag>
          ) : (
            <InlineSelect
              value={v as "regular" | "guest"}
              onSave={(next) => onInlineEdit(row, { user_type: next })}
            />
          ),
      },
      {
        title: "Balance",
        dataIndex: "balance_cents",
        key: "balance",
        width: 110,
        align: "right",
        sorter: true,
        sortOrder:
          sort === "balance"
            ? sortDir === "asc"
              ? "ascend"
              : "descend"
            : null,
        render: (v: number | null) => (
          <Text type={v !== null && v < 0 ? "danger" : undefined}>
            {formatCents(v)}
          </Text>
        ),
      },
      {
        title: (
          <Tooltip title="Cost to the venue of everything this user bought — Σ (qty × the buy price effective when each item was purchased). Counts only articles that have a recorded buy price.">
            Cost
          </Tooltip>
        ),
        dataIndex: "cost_of_goods_cents",
        key: "cost",
        width: 110,
        align: "right",
        responsive: ["lg"],
        sorter: true,
        sortOrder:
          sort === "cost"
            ? sortDir === "asc"
              ? "ascend"
              : "descend"
            : null,
        render: (v: number | null) => (
          <Text type="secondary">{v == null ? "—" : formatCents(v)}</Text>
        ),
      },
      {
        title: "Volunteer",
        key: "volunteer",
        width: 100,
        responsive: ["lg"],
        render: (_: unknown, row) => {
          const owed = owedMap.get(row.strichliste_user_id) ?? 0;
          return owed > 0 ? (
            <Tag color="orange">
              {owed} owed
            </Tag>
          ) : (
            <Text type="secondary">—</Text>
          );
        },
      },
      {
        title: "Activity",
        dataIndex: "last_activity",
        key: "last_activity",
        width: 110,
        // Below lg the sidebar overlay-collapses, so md effectively
        // gets phone-width content. Keep secondary columns off until lg.
        responsive: ["lg"],
        sorter: true,
        sortOrder:
          sort === "last_activity"
            ? sortDir === "asc"
              ? "ascend"
              : "descend"
            : null,
        render: (v: string | null) =>
          v ? (
            <Tooltip title={formatFullIso(v)}>{formatCompactDateTime(v)}</Tooltip>
          ) : (
            "—"
          ),
      },
      {
        title: "Status",
        key: "status",
        width: 110,
        filterDropdown: (p) => (
          <QuickFilterDropdown
            props={p}
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "disabled", label: "Disabled" },
            ]}
          />
        ),
        filteredValue: statusFilter ? [statusFilter] : null,
        render: (_: unknown, row) =>
          row.disabled ? (
            <Tag>Disabled</Tag>
          ) : !row.active ? (
            <Tag>Inactive</Tag>
          ) : (
            <Tag color="green">Active</Tag>
          ),
      },
    ],
    // onInlineEdit is recreated on every render; including it would
    // thrash the memo. The closure captures the latest `api`/`refresh`
    // via module scope and component state, so a stale reference is
    // not a concern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort, sortDir, filter, statusFilter, owedMap],
  );

  return (
    <Card>
      {/* Header: title + Add User. flex-wrap so the button drops below
          the title on narrow viewports instead of being shoved off the
          right edge. */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={2} style={{ marginBottom: 0 }}>
          Users
        </Title>
        <Button type="primary" onClick={() => setAddOpen(true)}>
          + Add user
        </Button>
      </div>
      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16, marginTop: 12 }}
          showIcon
        />
      )}
      {/* Filter row: search + type tabs. Search caps at 320px on wide
          viewports but goes full-width on mobile so the operator can
          actually see what they're typing. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12,
          marginTop: 12,
          marginBottom: 12,
        }}
      >
        <Input.Search
          placeholder="Search name / email / real name…"
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
          allowClear
          style={{ flex: "1 1 200px", maxWidth: 320 }}
        />
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            flex: "0 0 auto",
          }}
        >
          <Switch
            checked={showVouchers}
            onChange={(v) => setParam("vouchers", v ? "1" : undefined)}
          />
          Show vouchers
        </span>
        <Tabs
          activeKey={filter}
          onChange={(k) => setParam("type", k === "all" ? undefined : k)}
          items={[
            { key: "all", label: "All" },
            { key: "regular", label: "Regular" },
            { key: "guest", label: "Guest" },
          ]}
          // marginLeft: auto pushes the tabs to the right edge; when
          // the row wraps to a second line on narrow viewports the
          // tabs sit on their own row instead of being squashed.
          style={{ flex: "0 0 auto", marginLeft: "auto" }}
        />
        {/* Reset clears every list-state URL param in one click. Only
            enabled when there's something to clear so the affordance
            stays unobtrusive in the common case. */}
        <Button
          icon={<ClearOutlined />}
          disabled={!hasActiveFilters}
          onClick={() => {
            setQDraft("");
            setSearchParams(new URLSearchParams());
          }}
        >
          Reset
        </Button>
      </div>
      <Table<UserRow>
        columns={columns}
        dataSource={rows}
        rowKey="strichliste_user_id"
        loading={loading}
        // offsetHeader keeps the table header pinned UNDER the AppShell
        // top bar (admin + dark-mode toggle) instead of sliding over it.
        // 64px matches AntD's default Layout.Header height.
        sticky={{ offsetHeader: 64 }}
        onChange={onTableChange}
        onRow={(row) => ({
          onClick: () => navigate(`/users/${row.strichliste_user_id}`),
          style: { cursor: "pointer" },
        })}
        rowClassName={(row) =>
          row.user_type === "guest" ? "users-table-guest-row" : ""
        }
        pagination={false}
      />
      <AddUserModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false);
          void refresh();
        }}
      />
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Inline cell components — small, internal, save-on-blur.
// ---------------------------------------------------------------------------

function InlineText({
  value,
  placeholder,
  onSave,
}: {
  value: string;
  placeholder?: string;
  onSave: (next: string) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  if (!editing) {
    const display = value || placeholder || "—";
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        title={value || undefined}
        // `display: block` + min-width: 0 forces the cell layout to
        // truncate long real names with an ellipsis instead of pushing
        // the row width past the column. Hover shows the full value.
        style={{
          cursor: "text",
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {display}
      </span>
    );
  }
  return (
    <Input
      autoFocus
      size="small"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={() => {
        setEditing(false);
        if (draft !== value) void onSave(draft);
      }}
      onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
    />
  );
}

function InlineSelect({
  value,
  onSave,
}: {
  value: "regular" | "guest";
  onSave: (next: "regular" | "guest") => void | Promise<void>;
}) {
  // AntD Select so the row picks up the active theme (light + dark). The
  // wrapper stops the row-click navigate-to-detail handler from firing
  // when the operator opens the dropdown; block + 100% width lets the
  // Select fill the entire Type column cell instead of pinning to a
  // fixed pixel value.
  return (
    <span
      onClick={(e) => e.stopPropagation()}
      style={{ display: "block", width: "100%" }}
    >
      <Select<"regular" | "guest">
        size="small"
        value={value}
        style={{ width: "100%" }}
        onChange={(next) => {
          if (next !== value) void onSave(next);
        }}
        options={[
          { value: "regular", label: "Regular" },
          { value: "guest", label: "Guest" },
        ]}
      />
    </span>
  );
}
